/**
 * ListDragController — drag-and-drop reordering/reparenting for List View.
 *
 * Owns pointer/touch tracking, hit-testing (which row, or the "leave
 * without parent" zone, is under the pointer), drop-position visuals
 * (insertion line / target highlight), and auto-scroll near the list's
 * edges. Purely DOM/geometry — it reports the resolved action on drop via
 * a callback; ListView interprets that (store/GraphVisualizer mutation in
 * edit mode, or a local-only reorder in normal mode).
 *
 * Behavior (see docs discussed with the user):
 *   - Normal mode: reorder only, and only within the dragged node's
 *     current sibling list (same parent occurrence).
 *   - Edit mode: dropping between two rows inserts as a child of *that*
 *     row's parent at that position (a same-parent drop is a reorder, a
 *     different-parent drop is a reparent-with-position); dropping
 *     directly onto a row's middle band reparents onto it (appended
 *     last); dropping on the "leave without parent" zone orphans it.
 *   - Only the recipe's actual main root is never draggable (ListView
 *     doesn't call registerRow for it); any other top-level node — one
 *     orphaned by a previous drag, or a freshly created node with no
 *     parent yet — is draggable too, so it can be attached somewhere in
 *     the first place. Reparent/orphan targets must be in the same
 *     recipe and must not create a cycle.
 *   - Touch disambiguates a drag from a scroll via a long-press (see
 *     TOUCH_LONG_PRESS_MS): moving before it fires lets the page scroll
 *     natively instead.
 */

export type ListDragResult =
  | { kind: 'reorder'; nodeId: string; parentId: string; newOrder: string[] }
  | { kind: 'reparent'; nodeId: string; newParentId: string; insertIndex?: number }
  | { kind: 'orphan'; nodeId: string };

export interface ListDragCallbacks {
  /** Whether reparent/orphan drops are allowed. Normal mode only reorders. */
  isEditMode: () => boolean;
  /** Recipe id for a node id (row or parent-container id alike). */
  getRecipeId: (nodeId: string) => string | undefined;
  /** Would attaching nodeId under candidateParentId create a cycle? */
  wouldCreateCycle: (nodeId: string, candidateParentId: string) => boolean;
  /** Fired when a drag actually starts/ends (past the movement threshold) — ListView suspends its own re-rendering while true. */
  onDragStateChange: (dragging: boolean) => void;
  /** Fired with the resolved action on a valid drop. */
  onDrop: (result: ListDragResult) => void;
}

const DRAG_THRESHOLD = 5;
// Fraction of a row's height, top and bottom, treated as "insert
// before/after" rather than "reparent onto".
const EDGE_BAND = 0.28;
const AUTOSCROLL_MARGIN = 48;
const AUTOSCROLL_SPEED = 14;
// Touch needs to disambiguate "drag this row" from "scroll the list" —
// both start the same way (a finger down on a row). A long-press commits
// to dragging; moving more than the slop before it fires means the user
// is scrolling instead, so we bow out and let that happen natively.
const TOUCH_LONG_PRESS_MS = 200;
const TOUCH_LONG_PRESS_SLOP = 10;

export class ListDragController {
  private scrollContainer: HTMLElement;
  private callbacks: ListDragCallbacks;

  private startX = 0;
  private startY = 0;
  private dragging = false;
  private draggedNodeId: string | null = null;
  private draggedOriginParentId: string | null = null;
  private draggedRowEl: HTMLElement | null = null;

  private ghostEl: HTMLElement | null = null;
  private orphanZoneEl: HTMLElement | null = null;
  private highlightedEl: HTMLElement | null = null;
  private insertionLineEl: HTMLElement | null = null;

  private pendingResult: ListDragResult | null = null;

  private autoScrollDir: -1 | 0 | 1 = 0;
  private autoScrollRAF: number | null = null;

  constructor(scrollContainer: HTMLElement, callbacks: ListDragCallbacks) {
    this.scrollContainer = scrollContainer;
    this.callbacks = callbacks;
  }

  /** Whether a drag is currently in progress (past the movement threshold). */
  isActive(): boolean {
    return this.dragging;
  }

  /** If a drag is in progress, abort it with no effect. Call before a re-render replaces the DOM out from under it. */
  cancel(): void {
    this.cleanup();
  }

  /**
   * Wire drag-start listeners on a rendered row. Call for every draggable
   * row on every render (root nodes should never be registered).
   */
  registerRow(rowEl: HTMLElement, nodeId: string): void {
    const start = (clientX: number, clientY: number): void => {
      this.startX = clientX;
      this.startY = clientY;
      this.dragging = false;
      this.draggedNodeId = nodeId;
      this.draggedRowEl = rowEl;
      const parentContainer = rowEl.closest('[data-parent-id]') as HTMLElement | null;
      this.draggedOriginParentId = parentContainer?.dataset.parentId ?? null;
    };

    rowEl.addEventListener('mousedown', (e: MouseEvent) => {
      if (e.button !== 0) return;
      if ((e.target as HTMLElement).closest('button, .list-arrow')) return;
      e.preventDefault(); // stop the browser starting a text selection drag
      start(e.clientX, e.clientY);

      const moveHandler = (ev: MouseEvent) => {
        if (!this.dragging) {
          const dx = ev.clientX - this.startX;
          const dy = ev.clientY - this.startY;
          if (Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
          this.beginDrag();
        }
        this.handleMove(ev.clientX, ev.clientY);
      };
      const upHandler = () => {
        document.removeEventListener('mousemove', moveHandler);
        document.removeEventListener('mouseup', upHandler);
        this.handleUp();
      };
      document.addEventListener('mousemove', moveHandler);
      document.addEventListener('mouseup', upHandler);
    });

    // Native touch scrolling is decided by the browser as soon as the
    // *first* touchmove happens — by the time our long-press timer (or a
    // preventDefault called only once we're sure it's a drag) would ever
    // run, the browser may already have committed to scrolling, and a
    // later preventDefault can't undo that. touch-action: none avoids
    // the race entirely by telling the browser to never treat a touch
    // starting on this row as a scroll gesture; we then drive scrolling
    // ourselves (see 'scrolling' mode below) so the list still scrolls
    // normally when a touch on a row turns out to be a swipe, not a
    // long-press.
    rowEl.style.touchAction = 'none';

    rowEl.addEventListener('touchstart', (e: TouchEvent) => {
      if ((e.target as HTMLElement).closest('button, .list-arrow')) return;
      const touch = e.touches[0];
      if (!touch) return;
      const touchId = touch.identifier;
      start(touch.clientX, touch.clientY);

      let mode: 'pending' | 'scrolling' | 'dragging' = 'pending';
      let lastY = touch.clientY;

      let longPressTimer: number | null = window.setTimeout(() => {
        longPressTimer = null;
        if (mode !== 'pending') return; // already resolved as a scroll
        mode = 'dragging';
        this.beginDrag();
      }, TOUCH_LONG_PRESS_MS);

      const moveHandler = (ev: TouchEvent) => {
        const t = Array.from(ev.touches).find(t => t.identifier === touchId);
        if (!t) return;

        if (mode === 'dragging') {
          ev.preventDefault();
          this.handleMove(t.clientX, t.clientY);
          return;
        }

        if (mode === 'pending') {
          const dx = t.clientX - this.startX;
          const dy = t.clientY - this.startY;
          if (Math.hypot(dx, dy) <= TOUCH_LONG_PRESS_SLOP) return; // not resolved yet
          // Enough movement before the long-press fired — this is a
          // scroll, not a drag. Give up on dragging for this gesture.
          if (longPressTimer !== null) {
            window.clearTimeout(longPressTimer);
            longPressTimer = null;
          }
          mode = 'scrolling';
        }

        // mode === 'scrolling': drive the scroll ourselves, since
        // touch-action: none stops the browser from doing it natively.
        ev.preventDefault();
        this.scrollContainer.scrollTop += lastY - t.clientY;
        lastY = t.clientY;
      };
      const endHandler = () => {
        if (longPressTimer !== null) {
          window.clearTimeout(longPressTimer);
          longPressTimer = null;
        }
        rowEl.removeEventListener('touchmove', moveHandler);
        rowEl.removeEventListener('touchend', endHandler);
        rowEl.removeEventListener('touchcancel', endHandler);
        if (mode === 'dragging') this.handleUp();
      };
      rowEl.addEventListener('touchmove', moveHandler, { passive: false });
      rowEl.addEventListener('touchend', endHandler);
      rowEl.addEventListener('touchcancel', endHandler);
    }, { passive: true });
  }

  // ─── Movement / drop resolution ────────────────────────────────

  private beginDrag(): void {
    this.dragging = true;
    this.beginDragVisuals();
    this.callbacks.onDragStateChange(true);
  }

  private handleMove(clientX: number, clientY: number): void {
    if (!this.draggedNodeId || !this.dragging) return;
    this.updateGhostPosition(clientX, clientY);
    this.updateAutoScroll(clientY);
    this.resolveHover(clientX, clientY);
  }

  private handleUp(): void {
    if (this.dragging && this.pendingResult) {
      this.callbacks.onDrop(this.pendingResult);
    }
    this.cleanup();
  }

  private resolveHover(clientX: number, clientY: number): void {
    this.clearHoverVisuals();
    this.pendingResult = null;

    const draggedNodeId = this.draggedNodeId;
    if (!draggedNodeId) return;
    const editMode = this.callbacks.isEditMode();

    // "Leave without parent" zone (edit mode only)
    if (editMode && this.orphanZoneEl) {
      const zoneRect = this.orphanZoneEl.getBoundingClientRect();
      if (clientX >= zoneRect.left && clientX <= zoneRect.right && clientY >= zoneRect.top && clientY <= zoneRect.bottom) {
        this.orphanZoneEl.style.background = 'var(--accent)';
        this.orphanZoneEl.style.color = '#0d1117';
        this.pendingResult = { kind: 'orphan', nodeId: draggedNodeId };
        return;
      }
    }

    const el = document.elementFromPoint(clientX, clientY);
    const rowEl = (el as HTMLElement | null)?.closest?.('.list-node-item') as HTMLElement | null;
    if (!rowEl || rowEl === this.draggedRowEl) return;

    const hoveredNodeId = rowEl.dataset.nodeId;
    if (!hoveredNodeId || hoveredNodeId === draggedNodeId) return;

    const rect = rowEl.getBoundingClientRect();
    const relY = (clientY - rect.top) / rect.height;
    const isOnto = editMode && relY > EDGE_BAND && relY < (1 - EDGE_BAND);

    if (isOnto) {
      // Reparent onto hoveredNodeId — valid even when it's a top-level
      // (root or orphaned) row, since it doesn't need a parent context.
      if (this.callbacks.getRecipeId(draggedNodeId) !== this.callbacks.getRecipeId(hoveredNodeId)) return;
      if (this.callbacks.wouldCreateCycle(draggedNodeId, hoveredNodeId)) return;

      this.highlightedEl = rowEl;
      rowEl.style.outline = '2px solid var(--accent)';
      rowEl.style.outlineOffset = '-2px';
      this.pendingResult = { kind: 'reparent', nodeId: draggedNodeId, newParentId: hoveredNodeId };
      return;
    }

    // Insert before/after hoveredNodeId, as a child of hoveredParentId —
    // needs an actual parent context, so a top-level hovered row (no
    // `[data-parent-id]` ancestor) has no valid "between" position;
    // dropping near one only ever resolves via the "onto" branch above.
    const parentContainer = rowEl.closest('[data-parent-id]') as HTMLElement | null;
    const hoveredParentId = parentContainer?.dataset.parentId ?? null;
    if (!hoveredParentId) return;

    if (editMode) {
      if (this.callbacks.getRecipeId(draggedNodeId) !== this.callbacks.getRecipeId(hoveredParentId)) return;
      if (hoveredParentId !== draggedNodeId && this.callbacks.wouldCreateCycle(draggedNodeId, hoveredParentId)) return;
    } else if (hoveredParentId !== this.draggedOriginParentId) {
      return; // normal mode: only within the dragged node's own sibling list
    }

    const before = relY <= 0.5;
    this.showInsertionLine(rowEl, before);

    const siblingIds = Array.from(parentContainer!.children)
      .filter((c): c is HTMLElement => c instanceof HTMLElement && c.classList.contains('list-node-item'))
      .map(r => r.dataset.nodeId)
      .filter((id): id is string => !!id && id !== draggedNodeId);

    const hoveredIndex = siblingIds.indexOf(hoveredNodeId);
    const insertIndex = before ? hoveredIndex : hoveredIndex + 1;
    const newOrder = [...siblingIds];
    newOrder.splice(insertIndex, 0, draggedNodeId);

    if (editMode && hoveredParentId !== this.draggedOriginParentId) {
      this.pendingResult = { kind: 'reparent', nodeId: draggedNodeId, newParentId: hoveredParentId, insertIndex };
    } else {
      this.pendingResult = { kind: 'reorder', nodeId: draggedNodeId, parentId: hoveredParentId, newOrder };
    }
  }

  // ─── Visuals ────────────────────────────────────────────────────

  private beginDragVisuals(): void {
    if (!this.draggedRowEl) return;
    this.draggedRowEl.style.opacity = '0.35';

    // Belt-and-braces alongside preventDefault() on mousedown: stops text
    // selection from starting anywhere on the page while dragging.
    document.body.style.userSelect = 'none';

    const clone = this.draggedRowEl.cloneNode(true) as HTMLElement;
    clone.querySelectorAll('button, .list-arrow').forEach(el => el.remove());
    clone.style.position = 'fixed';
    clone.style.top = '0';
    clone.style.left = '0';
    clone.style.width = `${this.draggedRowEl.offsetWidth}px`;
    clone.style.pointerEvents = 'none';
    clone.style.zIndex = '1000';
    clone.style.opacity = '0.92';
    clone.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.5)';
    clone.style.transform = 'translate(-9999px, -9999px)';
    document.body.appendChild(clone);
    this.ghostEl = clone;

    if (this.callbacks.isEditMode()) {
      this.showOrphanZone();
    }
  }

  private showOrphanZone(): void {
    const zone = document.createElement('div');
    zone.textContent = '⊘ Deixa sense pare';
    zone.style.cssText = `
      position: fixed;
      left: 50%;
      bottom: 16px;
      transform: translateX(-50%);
      background: #1a1a2e;
      border: 2px dashed var(--accent-alpha-40);
      color: var(--accent);
      font-family: 'Courier New', Consolas, 'Liberation Mono', monospace;
      font-size: 13px;
      font-weight: bold;
      padding: 10px 20px;
      border-radius: 8px;
      z-index: 1001;
      pointer-events: none;
      transition: background 0.1s, color 0.1s;
    `;
    document.body.appendChild(zone);
    this.orphanZoneEl = zone;
  }

  private updateGhostPosition(clientX: number, clientY: number): void {
    if (this.ghostEl) {
      this.ghostEl.style.transform = `translate(${clientX + 12}px, ${clientY + 12}px)`;
    }
  }

  /** Anchor to insert "after" a row at — its own children container if expanded, else the row itself. */
  private getRowEndAnchor(rowEl: HTMLElement): HTMLElement {
    const next = rowEl.nextElementSibling;
    if (next instanceof HTMLElement && next.classList.contains('list-children')) {
      return next;
    }
    return rowEl;
  }

  private showInsertionLine(anchorRowEl: HTMLElement, before: boolean): void {
    if (!this.insertionLineEl) {
      this.insertionLineEl = document.createElement('div');
      this.insertionLineEl.style.cssText = 'height: 3px; background: var(--accent); border-radius: 2px; margin: 2px 4px; pointer-events: none;';
    }
    if (before) {
      anchorRowEl.insertAdjacentElement('beforebegin', this.insertionLineEl);
    } else {
      this.getRowEndAnchor(anchorRowEl).insertAdjacentElement('afterend', this.insertionLineEl);
    }
  }

  private clearHoverVisuals(): void {
    if (this.highlightedEl) {
      this.highlightedEl.style.outline = '';
      this.highlightedEl.style.outlineOffset = '';
      this.highlightedEl = null;
    }
    if (this.insertionLineEl?.parentNode) {
      this.insertionLineEl.parentNode.removeChild(this.insertionLineEl);
    }
    if (this.orphanZoneEl) {
      this.orphanZoneEl.style.background = '#1a1a2e';
      this.orphanZoneEl.style.color = 'var(--accent)';
    }
  }

  // ─── Auto-scroll ────────────────────────────────────────────────

  private updateAutoScroll(clientY: number): void {
    const rect = this.scrollContainer.getBoundingClientRect();
    if (clientY < rect.top + AUTOSCROLL_MARGIN) {
      this.autoScrollDir = -1;
    } else if (clientY > rect.bottom - AUTOSCROLL_MARGIN) {
      this.autoScrollDir = 1;
    } else {
      this.autoScrollDir = 0;
    }

    if (this.autoScrollDir !== 0 && this.autoScrollRAF === null) {
      const step = () => {
        if (this.autoScrollDir === 0 || !this.dragging) {
          this.autoScrollRAF = null;
          return;
        }
        this.scrollContainer.scrollTop += this.autoScrollDir * AUTOSCROLL_SPEED;
        this.autoScrollRAF = requestAnimationFrame(step);
      };
      this.autoScrollRAF = requestAnimationFrame(step);
    } else if (this.autoScrollDir === 0 && this.autoScrollRAF !== null) {
      cancelAnimationFrame(this.autoScrollRAF);
      this.autoScrollRAF = null;
    }
  }

  // ─── Cleanup ────────────────────────────────────────────────────

  private cleanup(): void {
    const wasDragging = this.dragging;

    if (this.draggedRowEl) this.draggedRowEl.style.opacity = '';
    document.body.style.userSelect = '';
    this.clearHoverVisuals();
    this.ghostEl?.remove();
    this.ghostEl = null;
    this.orphanZoneEl?.remove();
    this.orphanZoneEl = null;

    if (this.autoScrollRAF !== null) {
      cancelAnimationFrame(this.autoScrollRAF);
      this.autoScrollRAF = null;
    }
    this.autoScrollDir = 0;

    this.dragging = false;
    this.draggedNodeId = null;
    this.draggedOriginParentId = null;
    this.draggedRowEl = null;
    this.pendingResult = null;

    if (wasDragging) this.callbacks.onDragStateChange(false);
  }
}
